import io from "socket.io-client";
import api from "../services/api";

const isProd = process.env.NODE_ENV === "production";

// Extrai o "exp" (segundos) do payload do JWT sem validar assinatura.
const getTokenExp = (token) => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return payload.exp || 0;
  } catch (e) {
    return 0;
  }
};

// Token de acesso mais recente disponível. O interceptor do axios renova o token
// em api.defaults e/ou localStorage; o socket precisa usar sempre o mais novo,
// senão reconecta com o token de quando a página abriu (vence em 8h).
const getCurrentToken = () => {
  const candidates = [];

  const header = api.defaults.headers.Authorization;
  if (header && typeof header === "string") {
    candidates.push(header.replace(/^Bearer\s+/i, ""));
  }

  try {
    const stored = localStorage.getItem("token");
    if (stored) candidates.push(JSON.parse(stored));
  } catch (e) { }

  const valid = candidates.filter(t => t && typeof t === "string");
  if (!valid.length) return null;

  return valid.reduce((best, t) => (getTokenExp(t) > getTokenExp(best) ? t : best));
};

// Janela sem nova desconexão pelo servidor para considerar a conexão estável.
const STABLE_CONNECTION_MS = 30000;
const MAX_RECOVERY_DELAY_MS = 30000;

class SocketWorker {
  constructor(companyId, userId) {
    if (!SocketWorker.instance) {
      this.companyId = companyId
      this.userId = userId
      this.socket = null;
      this.eventListeners = {}; // Armazena os ouvintes de eventos registrados
      this.reconnectListeners = new Set();
      this.hasConnected = false;
      this.recovering = false;
      this.serverDisconnectCount = 0;
      this.stableTimer = null;
      this.configureSocket();
      SocketWorker.instance = this;
    }

    return SocketWorker.instance;
  }

  configureSocket() {
    this.socket = io(`${process.env.REACT_APP_BACKEND_URL}/${this?.companyId}`, {
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: Infinity,
      query: { userId: this.userId, token: `Bearer ${getCurrentToken()}` }
    });

    // Antes de cada reconexão automática, usa o token atual (pode ter sido renovado)
    this.socket.io.on("reconnect_attempt", () => {
      this.refreshQueryToken();
    });

    this.socket.on("connect", () => {
      if (!isProd) console.log("Conectado ao servidor Socket.IO");

      clearTimeout(this.stableTimer);
      this.stableTimer = setTimeout(() => {
        this.serverDisconnectCount = 0;
      }, STABLE_CONNECTION_MS);

      // Eventos emitidos enquanto estava desconectado foram perdidos:
      // avisa os componentes para ressincronizarem via HTTP.
      if (this.hasConnected) {
        this.reconnectListeners.forEach(cb => {
          try {
            cb();
          } catch (err) {
            if (!isProd) console.error("Erro em listener de reconexão:", err);
          }
        });
      }
      this.hasConnected = true;
    });

    this.socket.on("disconnect", (reason) => {
      if (!isProd) console.log("Desconectado do servidor Socket.IO:", reason);
      clearTimeout(this.stableTimer);

      // Quando o servidor derruba a conexão (ex.: token inválido/expirado) o
      // socket.io-client NÃO reconecta sozinho — é preciso renovar e reconectar.
      if (reason === "io server disconnect") {
        this.recoverFromServerDisconnect();
      } else {
        this.reconnectAfterDelay();
      }
    });

    this.socket.on("token:expired", () => {
      if (!isProd) console.warn("Token do socket expirado — renovando");
      // A desconexão pelo servidor vem logo em seguida e dispara a recuperação
    });

    // Reaplica listeners registrados antes de o socket ser recriado
    Object.keys(this.eventListeners).forEach(event => {
      this.eventListeners[event].forEach(cb => this.socket.on(event, cb));
    });
  }

  refreshQueryToken() {
    const token = getCurrentToken();
    if (this.socket && token) {
      this.socket.io.opts.query = {
        ...(this.socket.io.opts.query || {}),
        token: `Bearer ${token}`
      };
    }
  }

  async recoverFromServerDisconnect() {
    if (this.recovering) return;
    this.recovering = true;

    // Backoff para não entrar em loop se o servidor recusar repetidamente
    this.serverDisconnectCount += 1;
    const delay = Math.min(1000 * 2 ** (this.serverDisconnectCount - 1), MAX_RECOVERY_DELAY_MS);

    try {
      await new Promise(resolve => setTimeout(resolve, delay));

      const { data } = await api.post("/auth/refresh_token");
      if (data?.token) {
        localStorage.setItem("token", JSON.stringify(data.token));
        api.defaults.headers.Authorization = `Bearer ${data.token}`;
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        // Refresh token também inválido: sessão acabou de fato
        localStorage.removeItem("token");
        window.location.href = "/login";
        return;
      }
      // Falha de rede: tenta reconectar mesmo assim (próxima falha refaz o ciclo)
    } finally {
      this.recovering = false;
    }

    if (this.socket && !this.socket.connected) {
      this.refreshQueryToken();
      this.socket.connect();
    }
  }

  // Registra callback chamado a cada RECONEXÃO (não na primeira conexão)
  onReconnect(callback) {
    this.reconnectListeners.add(callback);
  }

  offReconnect(callback) {
    this.reconnectListeners.delete(callback);
  }

  // Adiciona um ouvinte de eventos
  on(event, callback) {

    this.connect();
    this.socket.on(event, callback);

    // Armazena o ouvinte no objeto de ouvintes
    if (!this.eventListeners[event]) {
      this.eventListeners[event] = [];
    }
    this.eventListeners[event].push(callback);
  }

  // Emite um evento
  emit(event, data) {
    this.connect();
    this.socket.emit(event, data);
  }

  // Desconecta um ou mais ouvintes de eventos
  off(event, callback) {
    this.connect();
    if (this.eventListeners[event]) {
      if (callback) {
        // Desconecta um ouvinte específico
        this.socket.off(event, callback);
        this.eventListeners[event] = this.eventListeners[event].filter(cb => cb !== callback);
      } else {
        // Desconecta todos os ouvintes do evento
        this.eventListeners[event].forEach(cb => this.socket.off(event, cb));
        delete this.eventListeners[event];
      }
    }
  }

  disconnect() {
    clearTimeout(this.stableTimer);
    this.reconnectListeners.clear();
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null
      if (!isProd) console.log("Socket desconectado manualmente");
    }
    // Permite criar uma nova instância (ex.: logout e login com outro usuário/empresa)
    SocketWorker.instance = null;
  }

  reconnectAfterDelay() {
    setTimeout(() => {
      if (!this.socket) {
        this.connect();
      } else if (!this.socket.connected && !this.socket.active) {
        // Reconexão automática desistiu/parou: força nova tentativa
        if (!isProd) console.log("Tentando reconectar após desconexão");
        this.refreshQueryToken();
        this.socket.connect();
      }
    }, 1000);
  }

  // Garante que o socket esteja conectado
  connect() {
    if (!this.socket) {
      this.configureSocket();
    }
  }

  forceReconnect() {
    if (this.socket && !this.socket.connected) {
      this.refreshQueryToken();
      this.socket.connect();
    }
  }
}

const instance = (companyId, userId) => new SocketWorker(companyId, userId);

export default instance;
